/**
 * Zustand store for Data Editor state management.
 * Handles selected rows, pending changes, and conflict resolution.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PendingChange } from '@/types/sensor';
import { ParameterOption } from '@/types/dataEditor';

interface DataEditorState {
  // Selected parameter for filtering data
  selectedParameter: ParameterOption | null;

  // Row selection state (keyed by timestamp or unique ID)
  selectedRows: Record<string, boolean>;

  // Pending changes tracking for optimistic UI
  pendingChanges: PendingChange[];

  // Flag for unsaved changes warning
  unsavedChanges: boolean;

  // Actions
  setSelectedParameter: (param: ParameterOption | null) => void;
  setSelectedRows: (rows: Record<string, boolean>) => void;
  toggleRowSelection: (rowId: string) => void;
  clearSelection: () => void;
  selectAll: (rowIds: string[]) => void;

  addPendingChange: (change: PendingChange) => void;
  updatePendingChange: (id: string, status: PendingChange['status']) => void;
  removePendingChange: (id: string) => void;
  clearPendingChanges: () => void;

  setUnsavedChanges: (value: boolean) => void;
}

export const useDataEditorStore = create<DataEditorState>()(
  persist(
    (set) => ({
      // Initial state
      selectedParameter: null,
      selectedRows: {},
      pendingChanges: [],
      unsavedChanges: false,
      // Parameter selection actions
      setSelectedParameter: (param) => set({ selectedParameter: param }),

      // Row selection actions
      setSelectedRows: (rows) => set({ selectedRows: rows }),

      toggleRowSelection: (rowId) =>
        set((state) => ({
          selectedRows: {
            ...state.selectedRows,
            [rowId]: !state.selectedRows[rowId],
          },
        })),

      clearSelection: () => set({ selectedRows: {} }),

      selectAll: (rowIds) =>
        set({
          selectedRows: rowIds.reduce(
            (acc, id) => ({ ...acc, [id]: true }),
            {}
          ),
        }),

      // Pending changes actions
      addPendingChange: (change) =>
        set((state) => {
          // Remove existing change with same ID if present
          const existingIndex = state.pendingChanges.findIndex(
            (c) => c.id === change.id
          );

          const newChanges =
            existingIndex >= 0
              ? [
                  ...state.pendingChanges.slice(0, existingIndex),
                  { ...change, timestamp: new Date() },
                  ...state.pendingChanges.slice(existingIndex + 1),
                ]
              : [...state.pendingChanges, { ...change, timestamp: new Date() }];

          return {
            pendingChanges: newChanges,
            unsavedChanges: true,
          };
        }),

      updatePendingChange: (id, status) =>
        set((state) => ({
          pendingChanges: state.pendingChanges.map((c) =>
            c.id === id ? { ...c, status, timestamp: new Date() } : c
          ),
        })),

      removePendingChange: (id) =>
        set((state) => ({
          pendingChanges: state.pendingChanges.filter((c) => c.id !== id),
          unsavedChanges: state.pendingChanges.filter((c) => c.id !== id)
            .length > 0,
        })),

      clearPendingChanges: () =>
        set({
          pendingChanges: [],
          unsavedChanges: false,
        }),

      setUnsavedChanges: (value) => set({ unsavedChanges: value }),
    }),
    {
      name: 'data-editor-store',
      // Only persist selected parameter, not transient selection/pending states
      partialize: () => ({}),
    }
  )
);
